import type {
  JsonPrimitive,
  JsonValue,
  ModelVariant,
  Provider,
  Registry,
  RegistryModelV4,
  RegistryOperationV4,
} from "./i2vRegistry.generated";

export type RegistryShadowDifference = {
  code:
    | "REGISTRY_V4_MISSING"
    | "REGISTRY_V4_PROVIDER_MISSING"
    | "REGISTRY_V4_MODEL_MISSING"
    | "REGISTRY_V4_OPERATION_MISSING"
    | "REGISTRY_V4_VALUE_DRIFT";
  provider_id?: string;
  model_id?: string;
  field: string;
  v3: JsonValue;
  v4: JsonValue;
};

export type RegistryShadowResult = {
  status: "match" | "drift" | "unavailable";
  catalog_digest: string | null;
  differences: RegistryShadowDifference[];
};

const normalized = (value: unknown): JsonValue => {
  if (Array.isArray(value)) {
    return value
      .map(normalized)
      .sort((left, right) =>
        JSON.stringify(left).localeCompare(JSON.stringify(right)),
      );
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, normalized(item)]),
    );
  }
  return value == null || ["string", "number", "boolean"].includes(typeof value)
    ? (value as JsonPrimitive)
    : String(value);
};

const equal = (left: unknown, right: unknown) =>
  JSON.stringify(normalized(left)) === JSON.stringify(normalized(right));

const parameter = (operation: RegistryOperationV4, id: string) =>
  operation.parameters.find((candidate) => candidate.parameter_id === id);

const inputPort = (operation: RegistryOperationV4, id: string) =>
  operation.input_ports.find((candidate) => candidate.port_id === id);

const legacyOperation = (model: ModelVariant) =>
  model.operations?.find((candidate) => candidate.id === "image_to_video");

const compareValue = (
  differences: RegistryShadowDifference[],
  providerId: string,
  modelId: string,
  field: string,
  v3: unknown,
  v4: unknown,
) => {
  if (equal(v3, v4)) return;
  differences.push({
    code: "REGISTRY_V4_VALUE_DRIFT",
    provider_id: providerId,
    model_id: modelId,
    field,
    v3: normalized(v3),
    v4: normalized(v4),
  });
};

const compareModel = (
  differences: RegistryShadowDifference[],
  provider: Provider,
  legacy: ModelVariant,
  current: RegistryModelV4,
) => {
  const v3 = legacyOperation(legacy);
  const v4 = current.operations.find(
    (candidate) => candidate.operation_id === "image_to_video",
  );
  if (!v3 || !v4) {
    differences.push({
      code: "REGISTRY_V4_OPERATION_MISSING",
      provider_id: provider.id,
      model_id: legacy.id,
      field: "operations.image_to_video",
      v3: Boolean(v3),
      v4: Boolean(v4),
    });
    return;
  }

  const contract = v3.input_contract;
  const features = contract.features ?? {};
  const durations = provider.labs?.duration_seconds.length
    ? provider.labs.duration_seconds
    : (contract.duration_seconds?.values ?? []);
  const resolutions = provider.labs?.output_presets.length
    ? provider.labs.output_presets.map((preset) => preset.id)
    : (contract.resolutions ?? []);
  const v4Duration = parameter(v4, "duration_seconds");
  const v4Resolution = parameter(v4, "resolution");
  const v4Aspect = parameter(v4, "aspect_ratio");

  compareValue(
    differences,
    provider.id,
    legacy.id,
    "duration_seconds.enum",
    durations,
    v4Duration?.enum_values ?? [],
  );
  compareValue(
    differences,
    provider.id,
    legacy.id,
    "duration_seconds.minimum",
    contract.duration_seconds?.minimum ?? null,
    v4Duration?.minimum ?? null,
  );
  compareValue(
    differences,
    provider.id,
    legacy.id,
    "duration_seconds.maximum",
    contract.duration_seconds?.maximum ?? null,
    v4Duration?.maximum ?? null,
  );
  compareValue(
    differences,
    provider.id,
    legacy.id,
    "resolution.enum",
    resolutions,
    v4Resolution?.enum_values ?? [],
  );
  compareValue(
    differences,
    provider.id,
    legacy.id,
    "aspect_ratio.enum",
    contract.aspect_ratio?.values ?? [],
    v4Aspect?.enum_values ?? [],
  );
  compareValue(
    differences,
    provider.id,
    legacy.id,
    "inputs.last_frame",
    Boolean(features.last_frame),
    Boolean(inputPort(v4, "last_frame")),
  );
  compareValue(
    differences,
    provider.id,
    legacy.id,
    "inputs.reference_images",
    Boolean(features.reference_images),
    Boolean(inputPort(v4, "reference_images")),
  );
  compareValue(
    differences,
    provider.id,
    legacy.id,
    "inputs.reference_images.maximum",
    features.reference_image_maximum ?? 0,
    inputPort(v4, "reference_images")?.maximum_items ?? 0,
  );
  for (const [legacyField, parameterId] of [
    ["negative_prompt", "negative_prompt"],
    ["native_audio", "generate_audio"],
    ["seed", "seed"],
  ] as const) {
    compareValue(
      differences,
      provider.id,
      legacy.id,
      `parameters.${parameterId}.adapter_consumed`,
      Boolean(features[legacyField]),
      Boolean(parameter(v4, parameterId)?.adapter_consumed),
    );
  }
  for (const [parameterId, options] of [
    ["workflow_id", provider.labs?.workflow_variants ?? []],
    ["lora_id", provider.labs?.lora_variants ?? []],
  ] as const) {
    compareValue(
      differences,
      provider.id,
      legacy.id,
      `parameters.${parameterId}.enum`,
      options.map((option) => option.id),
      parameter(v4, parameterId)?.enum_values ?? [],
    );
  }
};

export function compareRegistryV3V4(registry: Registry): RegistryShadowResult {
  const catalog = registry.catalog;
  if (!catalog || catalog.schema_version !== 4) {
    return {
      status: "unavailable",
      catalog_digest: null,
      differences: [
        {
          code: "REGISTRY_V4_MISSING",
          field: "catalog",
          v3: true,
          v4: false,
        },
      ],
    };
  }

  const differences: RegistryShadowDifference[] = [];
  for (const provider of registry.providers) {
    const route = catalog.providers.find(
      (candidate) => candidate.provider_id === provider.id,
    );
    if (!route) {
      differences.push({
        code: "REGISTRY_V4_PROVIDER_MISSING",
        provider_id: provider.id,
        field: "provider",
        v3: true,
        v4: false,
      });
      continue;
    }
    for (const legacy of provider.models ?? []) {
      const current = route.models.find(
        (candidate) => candidate.model_id === legacy.id,
      );
      if (!current) {
        differences.push({
          code: "REGISTRY_V4_MODEL_MISSING",
          provider_id: provider.id,
          model_id: legacy.id,
          field: "model",
          v3: true,
          v4: false,
        });
        continue;
      }
      compareModel(differences, provider, legacy, current);
    }
  }

  differences.sort((left, right) =>
    [left.provider_id, left.model_id, left.field, left.code]
      .join("/")
      .localeCompare(
        [right.provider_id, right.model_id, right.field, right.code].join("/"),
      ),
  );
  return {
    status: differences.length ? "drift" : "match",
    catalog_digest: catalog.catalog_digest,
    differences,
  };
}
